import { Component, Element, Event, EventEmitter, Host, Method, Prop, h } from '@stencil/core';
import {
  CheckableFormCompoment,
  getLabelText,
  hasText,
  HiddenFormElementSlot,
  connectForm,
  disconnectForm,
} from '../../utils';

/** @slot - Adds label text. */
@Component({
  shadow: true,
  styleUrl: 'switch.css',
  tag: 'abds-switch',
})
export class Switch implements CheckableFormCompoment {
  //--------------------------------------------------------------------------
  //
  //  Element
  //
  //--------------------------------------------------------------------------

  @Element() host: HTMLAbdsSwitchElement;

  //--------------------------------------------------------------------------
  //
  //  Props
  //
  //--------------------------------------------------------------------------

  /** Sets name attribute. */
  @Prop({ reflect: true }) name!: string;

  /** Sets checked state. */
  // eslint-disable-next-line @stencil/strict-mutable
  @Prop({ mutable: true, reflect: true }) checked = false;

  /** Disables switch interaction.  */
  @Prop({ reflect: true }) disabled = false;

  /** Sets tooltip text in embedded abds-label. */
  @Prop({ reflect: true }) tooltip: string;

  /**
   * Form submission value if selected. Default for checked checkbox is **on**:
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox#attr-value
   */
  // eslint-disable-next-line @stencil/strict-mutable
  @Prop({ mutable: true, reflect: true }) value = 'on';

  //--------------------------------------------------------------------------
  //
  //  Private Properties
  //
  //--------------------------------------------------------------------------

  /** toggle element */
  private toggleEl: HTMLDivElement;

  /** text to be used for label */
  private labelText: string;

  /** hidden input element */
  hiddenElement: HTMLInputElement;

  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------

  private toggle = (): void => {
    this.checked = !this.checked;
    this.setFocus();
    this.abdsChange.emit();
  };

  private onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === ' ') {
      event.preventDefault();

      if (!event.repeat) this.toggle();
    }
  };

  //--------------------------------------------------------------------------
  //
  //  Public Methods
  //
  //--------------------------------------------------------------------------

  /** Sets focus on native `input` in `abds-switch`. */
  @Method()
  async setFocus(): Promise<void> {
    this.toggleEl?.focus();
  }

  //--------------------------------------------------------------------------
  //
  //  Events
  //
  //--------------------------------------------------------------------------

  /** Fired when the element's value is changed. */
  @Event() abdsChange: EventEmitter;

  //--------------------------------------------------------------------------
  //
  //  Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  connectedCallback() {
    this.labelText = getLabelText(this.host);

    connectForm(this);
  }

  disconnectedCallback() {
    disconnectForm(this);
  }

  render() {
    return (
      <Host onClick={this.disabled ? null : this.toggle} onKeyDown={this.disabled ? null : this.onKeyDown}>
        <HiddenFormElementSlot component={this} type="checkbox" />
        <div
          aria-checked={this.checked.toString()}
          aria-label={this.labelText || null}
          class="switch-container"
          ref={(toggleEl) => (this.toggleEl = toggleEl)}
          role="switch"
          tabIndex={this.disabled ? undefined : 0}
        >
          <div class="icons-container">
            <abds-icon icon="check-1" size="md" stroke-width="bold"></abds-icon>
            <abds-icon icon="remove" size="md" stroke-width="bold"></abds-icon>
          </div>
          <span class="thumb"></span>
        </div>
        {hasText(this.host) && (
          <abds-label tooltip={this.tooltip} weight="medium">
            <slot />
          </abds-label>
        )}
      </Host>
    );
  }
}
